import './app-bar.scss';

import { Avatar, Stack, Typography } from '@mui/material';
import { ConvergeTooltip, useAuthContext } from '@converge/ui';
import { Link, useLocation } from 'react-router-dom';
import { MouseEvent, useCallback, useState } from 'react';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import { ConvergeGlobalSearchField } from '@converge/ui';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import RequireAuth from '@converge/ui/domain/auth/require-auth';
import SearchIcon from '@mui/icons-material/Search';
import Toolbar from '@mui/material/Toolbar';
import { config } from '@converge/config';
import { logoutUserSSO } from '@converge/ui/domain/auth/auth-provider';
import { useGetUsers } from '@converge/graphql';

export default function ConvergeAppBar() {
  const { resetUser, user } = useAuthContext();
  const { data } = useGetUsers({ username: user?.user.username || '' });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [isVisible, setIsVisible] = useState(false);
  const handleProfileMenuOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement> | any) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const currentUserInfo = data?.users.edges.filter(
    (currentUser) => currentUser.node.username === user?.user.username
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleLogoutEvent = useCallback(async (): Promise<void> => {
    resetUser();
    await logoutUserSSO();
  }, [resetUser]);

  const currentUserInfoValid = currentUserInfo && currentUserInfo.length > 0;
  const currentUserInfoNode = currentUserInfo && currentUserInfo[0]?.node;

  const menuId = 'primary-search-account-menu';
  const menu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem className="menu-avatar" data-testid="user-avatar">
        <Stack className="menu-avatar-container" spacing={1}>
          <Stack className="menu-avatar-user-firstName">
            {' '}
            {currentUserInfoValid && currentUserInfoNode?.firstName}{' '}
            {currentUserInfoValid && currentUserInfoNode?.lastName}{' '}
          </Stack>
          <Stack className="menu-avatar-converge"> Converge Insurance</Stack>
          <Stack className="menu-avatar-email">{user?.user.username}</Stack>
        </Stack>
      </MenuItem>
      {/* <MenuItem
        className="menu-avatar-profile"
        onClick={() => navigate.profile()}
      >
        Profile
      </MenuItem> */}
      <MenuItem className="menu-avatar-logout" onClick={handleLogoutEvent}>
        Log Out
      </MenuItem>
    </Menu>
  );
  const toggleVisibility = () => {
    setIsVisible((prevState) => !prevState);
  };

  enum env {
    Dev = 'development',
    Staging = 'staging',
    local = 'local',
    qa = 'qa',
  }

  const locationNow = useLocation().pathname.split('/');

  return (
    <>
      {' '}
      {config.convergeEnv.toString() === env.Dev && (
        <Stack className="dev-env">DEVELOPMENT ENVIRONMENT</Stack>
      )}
      {config.convergeEnv.toString() === env.Staging && (
        <Stack className="staging-env">STAGING ENVIRONMENT</Stack>
      )}
      {config.convergeEnv.toString() === env.qa && (
        <Stack className="qa-env">QA ENVIRONMENT</Stack>
      )}
      <AppBar position="static" elevation={1} className="main-app-static-bar">
        <Toolbar>
          <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
            <Stack data-testid="converge-logo">
              <ConvergeTooltip title={config.appTitle}>
                <img src={config.newLogo} className="AppBar-logo" alt="logo" />
              </ConvergeTooltip>
            </Stack>
          </Stack>
          <Typography className="link-container" data-testid="link-container">
            <Link to="/underwriting" data-testid="submissions-link">
              <Stack
                className={`submissions-link  ${
                  '/' + locationNow[1] === '/underwriting'
                    ? 'selected-tab'
                    : ' '
                }`}
              >
                Submissions
              </Stack>
            </Link>

            <Link to="/insureds" data-testid="insureds-link">
              <Stack
                className={`insureds-link ${
                  '/' + locationNow[1] === '/insureds' ? 'selected-tab' : ' '
                }`}
              >
                Insureds
              </Stack>
            </Link>
            <Link to="/claims" data-testid="claims-link">
              <Stack
                className={`claims-link ${
                  '/' + locationNow[1] === '/claims' ? 'selected-tab' : ' '
                }`}
              >
                Claims
              </Stack>
            </Link>
            <Link to="/producers/brokerages" data-testid="producers-link">
              <Stack
                className={`producers-link ${
                  '/' + locationNow[1] === '/producers' ? 'selected-tab' : ' '
                }`}
              >
                Producers
              </Stack>
            </Link>
          </Typography>
          <RequireAuth>
            <Box
              className={`box global-search-container ${
                isVisible ? 'show' : ''
              }`}
            >
              <ConvergeGlobalSearchField setIsVisible={setIsVisible} />
            </Box>
          </RequireAuth>
          <Box className="right-side-app-bar">
            <div className="AppBar-section-spacing">
              {isVisible === false && (
                <SearchIcon
                  onClick={toggleVisibility}
                  className="search-icon"
                />
              )}
              <NotificationsNoneOutlinedIcon className="notification-icon" />
              <Typography className="AppBar-username-text">
                <Avatar className="avatar-icon" onClick={handleProfileMenuOpen}>
                  {' '}
                  {user?.user.username?.slice(0, 2).toUpperCase()}
                </Avatar>
              </Typography>
            </div>
          </Box>
        </Toolbar>
      </AppBar>
      {/* {mobileMenu} */}
      {menu}
    </>
  );
}

ConvergeAppBar.Divider = () => (
  <Divider
    orientation="vertical"
    variant="middle"
    flexItem
    className="converge-app-bar-divider"
  />
);
